import type { CalendarDateLike } from './businessDate';

/**
 * カレンダー日付をYYYY-MM-DD形式の文字列に変換。
 * `Date` / `JstCalendarDate` のいずれも受け付ける。
 */
export const toDateString = (
  date: CalendarDateLike,
): `${number}-${number}-${number}` => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}` as `${number}-${number}-${number}`;
};

/**
 * 日付と時刻をISO8601形式（日本時間）に変換
 *
 * @param date - YYYY-MM-DD形式の日付
 * @param time - HH:mm形式の時刻
 * @returns ISO8601形式の日時文字列（日本時間: +09:00）
 * @example
 * toISO8601('2025-11-24', '10:30') // '2025-11-24T10:30:00+09:00'
 */
export const toISO8601 = (
  date: `${number}-${number}-${number}`,
  time: string,
): string => {
  return `${date}T${time}:00+09:00`;
};

/**
 * 2つのISO8601形式の日時文字列の差分を分で返す
 *
 * @param startDateTime - 開始日時（ISO8601形式、例: '2026-01-13T17:00:00+09:00'）
 * @param endDateTime - 終了日時（ISO8601形式、例: '2026-01-13T17:30:00+09:00'）
 * @returns 差分（分）。endDateTimeがstartDateTimeより前の場合は負の値
 * @example
 * getDiffMinutes('2026-01-13T17:00:00+09:00', '2026-01-13T17:30:00+09:00') // 30
 * getDiffMinutes('2026-01-13T17:00:00+09:00', '2026-01-13T16:30:00+09:00') // -30
 */
export const getDiffMinutes = (
  startDateTime: string,
  endDateTime: string,
): number => {
  const startDate = new Date(startDateTime);
  const endDate = new Date(endDateTime);
  const diffMs = endDate.getTime() - startDate.getTime();
  return Math.round(diffMs / (1000 * 60));
};

/**
 * ISO8601形式の日時文字列からHH:mm形式の時刻を取り出す
 *
 * @param isoString - ISO8601形式の日時文字列（例: '2026-01-12T15:20:00+09:00'）
 * @returns HH:mm形式の時刻文字列（例: '15:20'）
 * @example
 * extractTimeFromISO('2026-01-12T15:20:00+09:00') // '15:20'
 * extractTimeFromISO('2026-01-13T09:05:00+09:00') // '09:05'
 */
export const extractTimeFromISO = (isoString: string): string => {
  return isoString.slice(11, 16);
};
